import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
// Motion only. Every colour, size and position on this page still comes from
// the inline styles below; success.css adds nothing but the arrival.
import './success.css';

/**
 * Where Cashfree sends the shopper back to.
 *
 * This page used to render "Order Placed Successfully!" unconditionally. It
 * took no data and asked nothing, so a declined card, an abandoned payment and
 * a real purchase all produced the same green tick. A shopper could close the
 * tab believing they had ordered when no money had moved, and would only find
 * out when nothing arrived.
 *
 * It now reports the real state. The authority is the Cashfree webhook, which
 * writes orderPaymentStatus on the order; this page only reads it back.
 *
 * Three states, and "pending" is the one that matters. The shopper's browser
 * frequently gets back here before the webhook has reached the server, so "not
 * completed yet" is NOT failure -- announcing failure there would be the same
 * lie pointing the other way. Pending polls briefly and then settles into an
 * honest "still confirming" rather than guessing.
 */

const POLL_INTERVAL_MS = 2500;
const POLL_FOR_MS = 30000;

const PaymentStatus = () => {
  const { search } = useLocation();
  // Cashfree appends its own order reference to the return URL. Accept the
  // spellings it has used rather than betting everything on one.
  const params = new URLSearchParams(search);
  const cashfreeOrderId =
    params.get('order_id') || params.get('orderId') || params.get('cf_order_id');

  const [status, setStatus] = useState('pending');
  const [gaveUp, setGaveUp] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (!cashfreeOrderId) {
      setGaveUp(true);
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const check = async () => {
      try {
        const res = await fetch(
          `https://riymay.com/api/v1/user/payments/status/${encodeURIComponent(cashfreeOrderId)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` } }
        );
        const data = await res.json();
        if (cancelled) return;

        if (data.status === 'completed' || data.status === 'failed') {
          setStatus(data.status);
          return; // settled, stop polling
        }
      } catch (e) {
        // A network hiccup on the way back from a payment page is common.
        // Keep waiting; never turn a transport error into "your payment failed".
      }

      if (cancelled) return;

      if (Date.now() - startedAt.current > POLL_FOR_MS) {
        setGaveUp(true);
        return;
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    };

    check();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [cashfreeOrderId]);

  if (status === 'completed') {
    return (
      <div className="pay-status" style={{ textAlign: "center", marginTop: "50px" }}>
        <h1 className="pay-status-heading" style={{ color: "green" }}>
          <CheckCircle
            className="pay-status-icon"
            style={{
              display: 'inline-block',
              marginRight: '10px',
              verticalAlign: 'middle',
              width: '48px',
              height: '48px',
            }}
          />
          Order Placed Successfully!
        </h1>
        <p className="pay-status-note">Thank you for your purchase.</p>
        <p className="pay-status-actions">
          <Link to="/order-history" className="pay-status-link">View your orders</Link>
        </p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="pay-status" style={{ textAlign: "center", marginTop: "50px" }}>
        <h1 className="pay-status-heading pay-status-heading--failed">
          <XCircle
            className="pay-status-icon"
            style={{
              display: 'inline-block',
              marginRight: '10px',
              verticalAlign: 'middle',
              width: '48px',
              height: '48px',
            }}
          />
          Payment not completed
        </h1>
        <p className="pay-status-note">
          Your payment did not go through, and you have not been charged. Your bag
          is still saved.
        </p>
        <p className="pay-status-actions">
          <Link to="/cart" className="pay-status-link">Back to cart</Link>
        </p>
      </div>
    );
  }

  // Pending, or no reference in the URL at all.
  return (
    <div className="pay-status" style={{ textAlign: "center", marginTop: "50px" }}>
      <h1 className="pay-status-heading pay-status-heading--pending">
        <Loader
          className="pay-status-icon pay-status-icon--spin"
          style={{
            display: 'inline-block',
            marginRight: '10px',
            verticalAlign: 'middle',
            width: '48px',
            height: '48px',
          }}
        />
        Confirming your payment
      </h1>
      <p className="pay-status-note">
        {gaveUp
          ? 'This is taking longer than usual. Your order will appear in your order history as soon as it is confirmed — please do not pay again.'
          : 'One moment while we confirm this with your bank.'}
      </p>
      <p className="pay-status-actions">
        <Link to="/order-history" className="pay-status-link">Check your orders</Link>
      </p>
    </div>
  );
};

export default PaymentStatus;
